import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          origin_url: window.location.origin  // Send current origin for correct reset link
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al enviar el email');
      }
      
      setSent(true);
      toast.success('Email enviado');
    } catch (error) {
      toast.error(String(error?.message || 'Error al enviar'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-overlay flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-white/10">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4">
            {sent ? (
              <CheckCircle className="w-8 h-8 text-primary" />
            ) : (
              <Mail className="w-8 h-8 text-primary" />
            )}
          </div>
          <CardTitle className="font-heading text-2xl uppercase">
            {sent ? 'Email Enviado' : 'Recuperar Contraseña'}
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          {sent ? (
            <div className="space-y-4 text-center">
              <p className="text-muted-foreground">
                Si el email <span className="text-white font-medium">{email}</span> está registrado, 
                recibirás un enlace para restablecer tu contraseña.
              </p>
              <p className="text-sm text-muted-foreground">
                El enlace expira en <span className="text-primary">2 horas</span>.
              </p>
              <p className="text-sm text-muted-foreground">
                Revisa también la carpeta de spam.
              </p>
              
              <div className="pt-4 space-y-2">
                <Button
                  variant="outline"
                  className="w-full border-white/10"
                  onClick={() => setSent(false)}
                >
                  Enviar a otro email
                </Button>
                <Link to="/">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Volver al inicio
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground text-center mb-4">
                Introduce tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-password-email"
                  className="bg-black/20 border-white/10 focus:border-primary"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full font-bold uppercase tracking-wide"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar enlace'
                )}
              </Button>
              
              <Link to="/">
                <Button variant="ghost" className="w-full mt-2">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al inicio
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
